import isEmpty from 'lodash/isEmpty';
import { clearCurrentUser, fetchCurrentUser } from './user.duck';
import {
  createUserWithIdp,
  emailValid as validateUserEmail,
  usersAPI,
  airtableAPI,
  brevoAPI,
  emailAPI,
} from '../util/api';
import { storableError } from '../util/errors';
import { currentLocale } from '../util/locale';
import { shutdown as shutdownIntercomSession } from '@intercom/messenger-js-sdk';
import * as log from '../util/log';
import { disconnectGlobalSendbird } from './sendbird.duck';

const authenticated = authInfo => authInfo && authInfo.isAnonymous === false;

// ================ Action types ================ //

export const AUTH_INFO_REQUEST = 'app/Auth/AUTH_INFO_REQUEST';
export const AUTH_INFO_SUCCESS = 'app/Auth/AUTH_INFO_SUCCESS';

export const LOGIN_REQUEST = 'app/Auth/LOGIN_REQUEST';
export const LOGIN_SUCCESS = 'app/Auth/LOGIN_SUCCESS';
export const LOGIN_ERROR = 'app/Auth/LOGIN_ERROR';

export const LOGOUT_REQUEST = 'app/Auth/LOGOUT_REQUEST';
export const LOGOUT_SUCCESS = 'app/Auth/LOGOUT_SUCCESS';
export const LOGOUT_ERROR = 'app/Auth/LOGOUT_ERROR';

export const SIGNUP_REQUEST = 'app/Auth/SIGNUP_REQUEST';
export const SIGNUP_SUCCESS = 'app/Auth/SIGNUP_SUCCESS';
export const SIGNUP_ERROR = 'app/Auth/SIGNUP_ERROR';

export const CONFIRM_REQUEST = 'app/Auth/CONFIRM_REQUEST';
export const CONFIRM_SUCCESS = 'app/Auth/CONFIRM_SUCCESS';
export const CONFIRM_ERROR = 'app/Auth/CONFIRM_ERROR';

export const EMAIL_VALID_REQUEST = 'app/auth/EMAIL_VALID_REQUEST';
export const EMAIL_VALID_SUCCESS = 'app/auth/EMAIL_VALID_SUCCESS';
export const EMAIL_VALID_ERROR = 'app/auth/EMAIL_VALID_ERROR';

// Generic user_logout action that can be handled elsewhere
// E.g. src/reducers.js clears store as a consequence
export const USER_LOGOUT = 'app/USER_LOGOUT';

// ================ Reducer ================ //

const initialState = {
  isAuthenticated: false,

  // scopes associated with current token
  authScopes: [],

  // auth info
  authInfoLoaded: false,

  // login
  loginError: null,
  loginInProgress: false,

  // logout
  logoutError: null,
  logoutInProgress: false,

  // signup
  signupError: null,
  signupInProgress: false,

  // confirm (create use with idp)
  confirmError: null,
  confirmInProgress: false,

  // validate email
  isEmailValid: false,
  emailValidError: null,
  emailValidInProgress: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case AUTH_INFO_REQUEST:
      return state;
    case AUTH_INFO_SUCCESS:
      return {
        ...state,
        authInfoLoaded: true,
        isAuthenticated: authenticated(payload),
        authScopes: payload.scopes,
      };

    case LOGIN_REQUEST:
      return {
        ...state,
        loginInProgress: true,
        loginError: null,
        logoutError: null,
        signupError: null,
      };
    case LOGIN_SUCCESS:
      return { ...state, loginInProgress: false, isAuthenticated: true };
    case LOGIN_ERROR:
      return { ...state, loginInProgress: false, loginError: payload };

    case LOGOUT_REQUEST:
      return { ...state, logoutInProgress: true, loginError: null, logoutError: null };
    case LOGOUT_SUCCESS:
      return { ...state, logoutInProgress: false, isAuthenticated: false, authScopes: [] };
    case LOGOUT_ERROR:
      return { ...state, logoutInProgress: false, logoutError: payload };

    case SIGNUP_REQUEST:
      return { ...state, signupInProgress: true, loginError: null, signupError: null };
    case SIGNUP_SUCCESS:
      return { ...state, signupInProgress: false };
    case SIGNUP_ERROR:
      return { ...state, signupInProgress: false, signupError: payload };

    case CONFIRM_REQUEST:
      return { ...state, confirmInProgress: true, loginError: null, confirmError: null };
    case CONFIRM_SUCCESS:
      return { ...state, confirmInProgress: false, isAuthenticated: true };
    case CONFIRM_ERROR:
      return { ...state, confirmInProgress: false, confirmError: payload };

    case EMAIL_VALID_REQUEST:
      return { ...state, emailValidInProgress: true, emailValidError: null, isEmailValid: false };
    case EMAIL_VALID_SUCCESS:
      return {
        ...state,
        emailValidInProgress: false,
        emailValidError: null,
        isEmailValid: payload.isValid,
      };
    case EMAIL_VALID_ERROR:
      return {
        ...state,
        emailValidInProgress: false,
        emailValidError: payload,
        isEmailValid: false,
      };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const authenticationInProgress = state => {
  const { loginInProgress, logoutInProgress, signupInProgress, confirmInProgress } = state.Auth;
  return loginInProgress || logoutInProgress || signupInProgress || confirmInProgress;
};

// ================ Action creators ================ //

export const authInfoRequest = () => ({ type: AUTH_INFO_REQUEST });
export const authInfoSuccess = info => ({ type: AUTH_INFO_SUCCESS, payload: info });

export const loginRequest = () => ({ type: LOGIN_REQUEST });
export const loginSuccess = () => ({ type: LOGIN_SUCCESS });
export const loginError = error => ({ type: LOGIN_ERROR, payload: error, error: true });

export const logoutRequest = () => ({ type: LOGOUT_REQUEST });
export const logoutSuccess = () => ({ type: LOGOUT_SUCCESS });
export const logoutError = error => ({ type: LOGOUT_ERROR, payload: error, error: true });

export const signupRequest = () => ({ type: SIGNUP_REQUEST });
export const signupSuccess = () => ({ type: SIGNUP_SUCCESS });
export const signupError = error => ({ type: SIGNUP_ERROR, payload: error, error: true });

export const confirmRequest = () => ({ type: CONFIRM_REQUEST });
export const confirmSuccess = () => ({ type: CONFIRM_SUCCESS });
export const confirmError = error => ({ type: CONFIRM_ERROR, payload: error, error: true });

export const emailValidRequest = () => ({ type: EMAIL_VALID_REQUEST });
export const emailValidSuccess = response => ({
  type: EMAIL_VALID_SUCCESS,
  payload: response.data,
});
export const emailValidError = error => ({
  type: EMAIL_VALID_ERROR,
  payload: error,
  error: true,
});

export const userLogout = () => ({ type: USER_LOGOUT });

// ================ Thunks ================ //

export const authInfo = () => (dispatch, getState, sdk) => {
  dispatch(authInfoRequest());
  return sdk
    .authInfo()
    .then(info => dispatch(authInfoSuccess(info)))
    .catch(e => {
      // Requesting auth info just reads the token from the token
      // store (i.e. cookies), and should not fail in normal
      // circumstances. If it fails, it's due to a programming
      // error. In that case we mark the operation done and dispatch
      // `null` success action that marks the user as unauthenticated.
      log.error(e, 'auth-info-failed');
      dispatch(authInfoSuccess(null));
    });
};

export const login = (username, password) => (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(loginRequest());

  // Note that the thunk does not reject when the login fails, it
  // just dispatches the login error action.
  return sdk
    .login({ username, password })
    .then(() => dispatch(loginSuccess()))
    .then(() => dispatch(fetchCurrentUser()))
    .catch(e => dispatch(loginError(storableError(e))));
};

export const logout = () => (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(logoutRequest());

  // Disconnect global SendBird before logout
  dispatch(disconnectGlobalSendbird());

  // Note that the thunk does not reject when the logout fails, it
  // just dispatches the logout error action.
  return sdk
    .logout()
    .then(() => {
      // The order of the dispatched actions
      dispatch(logoutSuccess());
      dispatch(clearCurrentUser());
      log.clearUserId();
      dispatch(userLogout());

      // Clear Intercom session
      shutdownIntercomSession();
    })
    .catch(e => dispatch(logoutError(storableError(e))));
};

/**
 * Save the current user's ID to their public data.
 * Uses Integration SDK via server to avoid Marketplace API optimistic locking (409 conflicts).
 */
const saveCurrentUserPublicId = (sdk, userId) =>
  usersAPI.updateProfile({
    id: userId,
    publicData: { id: userId },
  });

export const signup = params => (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(signupRequest());
  const {
    email,
    password,
    firstName,
    lastName,
    publicData,
    protectedData,
    organizationPrivateData,
    organizationName,
    newsletterOptIn,
  } = params;

  // Merge email + newsletter consent (if opted in) into protectedData.
  // We use protectedData (not privateData) for the newsletter flag
  // because sdk.currentUser.create() accepts protectedData on the
  // request body but silently drops privateData — keeping it here
  // means the flag is live the moment the account exists, and the
  // post-signup redirect won't race a follow-up updateProfile call.
  const finalProtectedData = {
    email,
    ...(protectedData || {}),
    ...(newsletterOptIn ? { newsletterSubscribed: true } : {}),
  };

  const createUserParams = {
    email,
    password,
    firstName,
    lastName,
    // The language the person signed up in, for emails (C40).
    publicData: { ...publicData, locale: currentLocale() },
    protectedData: finalProtectedData,
  };

  // We must login the user if signup succeeds since the API doesn't
  // do that automatically.
  return sdk.currentUser
    .create(createUserParams)
    .then(response => {
      dispatch(signupSuccess());

      const currentUser = response.data.data;
      return currentUser;
    })
    .then(async currentUser => {
      // First login the user
      await dispatch(login(email, password));
      try {
        await saveCurrentUserPublicId(sdk, currentUser.id.uuid);
      } catch (idError) {
        console.warn('Failed to save user public ID:', idError);
      }

      // Side-effect integrations fire-and-forget — none of them are
      // required for the user to proceed and all of them are slow
      // external calls. Awaiting them serially would keep the user
      // staring at a spinner for a noticeable beat. Failures still
      // get logged (and stay non-fatal, like before).
      //
      // Beta / community mode signups feed:
      //   - Airtable                      (internal team record)
      //   - Brevo list 'community' (#12)  (every forum member)
      //   - Brevo list 'newsletter' (#13) — only when the user
      //     opted in on the signup form
      const lang = currentLocale();
      if (process.env.REACT_APP_BETA_MODE === 'true') {
        airtableAPI
          .addUser({ organization: organizationName || null })
          .catch(e => console.warn('Failed to add user to Airtable:', e?.message));

        brevoAPI
          .addContact({
            firstName,
            lastName,
            email,
            lang,
            createdAt: new Date().toISOString(),
            list: 'community',
          })
          .catch(e => console.warn('Failed to add user to Brevo community list:', e?.message));

        if (newsletterOptIn) {
          brevoAPI
            .addContact({
              firstName,
              lastName,
              email,
              lang,
              createdAt: new Date().toISOString(),
              list: 'newsletter',
            })
            .catch(e => console.warn('Failed to add user to Brevo newsletter list:', e?.message));
        }
      }

      // Welcome email is also a best-effort send.
      emailAPI.user
        .welcome({ lang })
        .catch(e => console.warn('Failed to send welcome email:', e?.message));

      // Then save privateData (per-org subscription record) via server endpoint
      if (organizationPrivateData) {
        try {
          await usersAPI.updateProfile({
            id: currentUser.id.uuid,
            privateData: organizationPrivateData,
          });
        } catch (error) {
          console.warn('Failed to save organization privateData during signup:', error);
          // Don't fail signup if privateData save fails, but warn
        }
      }

      return currentUser;
    })
    .catch(e => {
      dispatch(signupError(storableError(e)));
      // No personal details in error reports.
      log.error(e, 'signup-failed');
    });
};

export const signupWithIdp = params => (dispatch, getState, sdk) => {
  dispatch(confirmRequest());
  const { organizationPrivateData, organizationName, ...idpParams } = params;
  const createUserParams = {
    ...idpParams,
    publicData: { ...idpParams.publicData, locale: currentLocale() },
  };

  return createUserWithIdp(createUserParams)
    .then(res => {
      return dispatch(confirmSuccess());
    })
    .then(async () => {
      // Fetch the current user first
      await dispatch(fetchCurrentUser());

      // Get current user from state
      const state = getState();
      const currentUser = state.user.currentUser;

      // Add user to Airtable (beta/production only)
      if (process.env.REACT_APP_BETA_MODE === 'true' && currentUser) {
        try {
          // The server takes the name and email from the signed-in account.
          await airtableAPI.addUser({ organization: organizationName || null });
        } catch (airtableError) {
          console.warn('Failed to add user to Airtable during IDP signup:', airtableError);
        }
      }

      // Then save privateData (per-org subscription record) via server endpoint
      if (organizationPrivateData) {
        try {
          if (currentUser && currentUser.id) {
            try {
              await usersAPI.updateProfile({
                id: currentUser.id.uuid,
                privateData: organizationPrivateData,
              });
            } catch (error) {
              console.warn('Failed to save organization privateData during IDP signup:', error);
              // Don't fail signup if privateData save fails, but warn
            }
          }
        } catch (error) {
          console.warn('Error saving organization privateData during IDP signup:', error);
          // Don't fail signup if privateData save fails
        }
      }
    })
    .catch(e => {
      log.error(e, 'create-user-with-idp-failed', { params });
      return dispatch(confirmError(storableError(e)));
    });
};

export const emailValid = email => (dispatch, getState, sdk) => {
  dispatch(emailValidRequest());

  return validateUserEmail({ email })
    .then(response => {
      dispatch(emailValidSuccess(response));
      return response;
    })
    .catch(e => dispatch(emailValidError(storableError(e))));
};
